import type { ConversationMessage } from '@/components/session/ConversationTranscript';
import type { TranscriptionSegment } from '@/types/session';

/**
 * Build ordered conversation messages from live_conversation_segments.
 *
 * Segments are the single source of truth for the transcript (the
 * smart_review_result blob is retired). Corrected diarization lives on each
 * segment's speaker_label, so this reflects smart-review output too. Sorted by
 * start_time (chronological), matching the server read ordering.
 */
export const messagesFromSegments = (segments: TranscriptionSegment[]): ConversationMessage[] => {
  return [...segments]
    .sort((a, b) => (a.start_time ?? 0) - (b.start_time ?? 0))
    .map((seg, idx) => ({
      id: seg.segment_guid || `loaded-${seg.id ?? idx}`,
      // Prefer speaker_label (what we persist); fall back to legacy role. Default patient.
      speaker: seg.speaker_label === 'doctor' || seg.role === 'doctor' || seg.role === 'DOCTOR' ? 'doctor' : 'patient',
      text: seg.text || '',
      timestamp: seg.start_time ?? 0,
      end_timestamp: seg.end_time,
    }));
};

/**
 * Concatenate segment text for single-speaker (dictation) restore, sorted
 * chronologically and de-duplicated by text.
 */
export const textFromSegments = (segments: TranscriptionSegment[]): string => {
  const seen = new Set<string>();
  return [...segments]
    .sort((a, b) => (a.start_time ?? 0) - (b.start_time ?? 0))
    .map((s) => (s.text || '').trim())
    .filter((t) => {
      if (!t || seen.has(t)) return false;
      seen.add(t);
      return true;
    })
    .join(' ');
};
